import type { FactBox, Read } from "@/components/ui/box.schema";
import type { InboxChannel, InboxConnection, InboxContext } from "@/lib/api/inbox";
import { inboxWords as words } from "./inbox-copy";

/** Only a server-supplied Google consent destination can leave this connection record. */
export function consentDestination(connection: InboxConnection): string | null {
  if (!connection.consent_url) return null;
  try {
    const destination=new URL(connection.consent_url);
    return destination.protocol==="https:" && destination.hostname==="accounts.google.com" && !destination.username && !destination.password
      ? destination.href:null;
  } catch {return null;}
}
function channelSentence(channel: InboxChannel, connection: InboxConnection): string {
  const provider=channel==="email"?words.gmail:words.googleChat;
  const status=connection.status==="active" ? (connection.mode==="preview"?words.localPreview:words.connected)
    :connection.status==="reconnect_required"?words.reconnectNeeded:words.notSetUp;
  return `${channel==="email"?words.email:words.chat}: ${provider}, ${status}`;
}

/** CASE-044: this is IMAM's connection record, not a read of a provider mailbox. */
export function connectionsBox(read: Read<InboxContext>): FactBox<InboxContext> {
  const value=read.status==="ready"?read.value:read.status==="stale"?read.last:null;
  const reconnect=value?(["email","chat"] as const).filter(channel=>value.channels[channel].status==="reconnect_required"):[];
  const next=reconnect.map(channel=>({channel,to:consentDestination(value!.channels[channel])})).find(item=>item.to);
  const summary=(context:InboxContext)=>{
    const channels=[channelSentence("email",context.channels.email),channelSentence("chat",context.channels.chat)].join(" · ");
    const missing=(["email","chat"] as const).filter(channel=>context.channels[channel].status==="reconnect_required" && !consentDestination(context.channels[channel]));
    return channels+(missing.length?` ${words.askForReconnect(missing.map(channel=>channel==="email"?words.gmail:words.googleChat).join(words.and))}`:"");
  };
  return {
    id:"connections",kind:"fact",label:words.messagingConnections,job:words.readConnections,owner:{system:"imam"},
    read,readTime:true,fold:"choice",
    act:next?.to?{does:"leave",verb:words.reconnectNewTab(next.channel==="email"?words.gmail:words.googleChat),providerUrl:next.to}:undefined,
    prose:context=>{
      const needed=(["email","chat"] as const).filter(channel=>context.channels[channel].status==="reconnect_required");
      if(needed.length)return words.needsReconnect(needed.map(channel=>channel==="email"?words.gmail:words.googleChat).join(words.and));
      if(Object.values(context.channels).some(channel=>channel.mode==="preview"))return words.previewConnections;
      if(Object.values(context.channels).every(channel=>channel.status==="active"))return words.allConnected;
      return words.connectionsNotSetUp;
    },
    detail:summary,openDetail:summary,
  };
}
